import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type {
  HttpApiServerParameterType,
  HttpApiServerRequestBodyType,
} from '@/services/http-api/httpApi';
import type { SocketIOServerParameterType } from '@/services/socket-io-api/socketIOApi';
import type { WebSocketApiServerParameterType } from '@/services/websocket-api/webSocketApi';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import type { AuthType, SecuritySchemeUseCase } from '@tarslib/types/lib/auth';
import { AuthValue } from '@tarslib/types/lib/auth';
import { ModelType } from '@tarslib/types/lib/common';
import { HttpApiRequestBodyType, ParametersLocationType } from '@tarslib/types/lib/httpApi';
import clsx from 'clsx';
import React from 'react';
import { Authorization, isAuthInherit } from '../authorization/Authorization';
import { ParametersCard } from './components/parameters-card/ParametersCard';
import { RequestBody } from './components/request-body/RequestBody';

export type RequestParameterProps = {
  value:
    | {
        type: ModelType.WebSocketApi;
        parameters: WebSocketApiServerParameterType;
      }
    | {
        id: number;
        type: ModelType.HttpApi;
        parameters: HttpApiServerParameterType;
        requestBody: HttpApiServerRequestBodyType;
        auth: AuthType | undefined;
        securityScheme?: SecuritySchemeUseCase;
      }
    | {
        type: ModelType.SOCKET_IO_API;
        parameters: SocketIOServerParameterType;
      };
  className?: string;
};

export function RequestParameter(props: RequestParameterProps) {
  const { value, className } = props;

  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const inheritAuthInfo = useDocsDataModelStore(
    (x) => (x.resourceData.extraData as ResourceHttpApiData['extraData']).inheritAuthInfo,
  );

  const hasAuth = React.useMemo(() => {
    if (value.type !== ModelType.HttpApi) {
      return false;
    }
    const isInherit = isAuthInherit(value?.auth);
    const auth = isInherit ? inheritAuthInfo?.auth : value?.auth;

    if (!auth?.type) {
      return false;
    }

    if (auth.type === AuthValue.NoAuth) return false;
    return true;
  }, [inheritAuthInfo?.auth, value]);

  const parameterValue = React.useMemo(() => {
    return [
      {
        type: ParametersLocationType.Path as const,
        data: value.parameters.path,
      },
      {
        type: ParametersLocationType.Query as const,
        data: value.parameters.query,
      },
      {
        type: ParametersLocationType.Header as const,
        data: value.parameters.header,
      },
      {
        type: ParametersLocationType.Cookie as const,
        data: value.parameters.cookie,
      },
    ].filter((x) => x.data.length);
  }, [value]);
  const hasParameter = React.useMemo(() => {
    return !!parameterValue.length;
  }, [parameterValue]);
  const hasRequestBody = React.useMemo(() => {
    return (
      value.type === ModelType.HttpApi && value.requestBody.type !== HttpApiRequestBodyType.None
    );
  }, [value]);
  const noneParameter = React.useMemo(() => {
    return !hasAuth && !hasParameter && !hasRequestBody;
  }, [hasAuth, hasParameter, hasRequestBody]);

  return (
    <div className={clsx('', className)}>
      <h2 className="mb-4 text-xl font-600">{clientI18n('api.label.parameters')}</h2>

      {noneParameter && <div>{clientI18n('parameter.count.none')}</div>}

      {hasAuth && value.type === ModelType.HttpApi && (
        <div
          className={clsx({
            ['mb-4']: hasParameter || hasRequestBody,
          })}
        >
          <Authorization id={value.id} securityScheme={value.securityScheme} auth={value.auth} />
        </div>
      )}

      {hasParameter && (
        <div
          className={clsx('flex flex-col gap-4', {
            ['mb-4']: hasRequestBody,
          })}
        >
          {parameterValue.map((x) => {
            return <ParametersCard viewMode="write" key={x.type} type={x.type} value={x.data} />;
          })}
        </div>
      )}

      {hasRequestBody && value.type === ModelType.HttpApi && (
        <RequestBody value={value.requestBody} />
      )}
    </div>
  );
}
